import { Link, type LinkOptions } from '@tanstack/react-router'
import { ChevronRight } from 'lucide-react'
import { createContext, useContext, useId, type ReactNode } from 'react'
import type { Art } from '~/art'
import { cx } from './cx'
import { Stage } from './Stage'

/** The area a page belongs to ("Kader"), set once by the shell; PageHeader shows it above the title. */
const Eyebrow = createContext<string | null>(null)
export const PageArea = Eyebrow.Provider

type PageHeaderProps = {
  title: ReactNode
  /** Small accent line above the title. Default: the page's area from PageArea; false hides it. */
  eyebrow?: ReactNode | false
  /** One line of context under the title ("Herren · Berlin-Liga · Saison 2026/27"). */
  description?: ReactNode
  breadcrumb?: ReactNode
  /** Leading visual next to the title (club logo, avatar). */
  leading?: ReactNode
  /**
   * Right-aligned actions. Rule (DESIGN.md §1): at most ONE primary Button; everything else goes into a
   * `Menu` ("…") or a secondary button.
   */
  actions?: ReactNode
  /** Tabs or filters directly under the header. */
  children?: ReactNode
  /**
   * A motif from ~/art: title, description, leading and actions then sit on a Stage (white text over a
   * scrim). For pages with one subject (a person, a match), not for lists. Actions use light/glass buttons.
   */
  art?: Art
  /** object-position of the art. */
  artFocus?: string
  /** Decoration on the stage behind the text, e.g. a large shirt number. */
  artAside?: ReactNode
}

export function PageHeader({ title, eyebrow, description, breadcrumb, leading, actions, children, art, artFocus, artAside }: PageHeaderProps) {
  const area = useContext(Eyebrow)
  // The area line is redundant on the area's own page and next to breadcrumbs, which name it already.
  const line = eyebrow === false ? null : (eyebrow ?? (!breadcrumb && area && area !== title ? area : null))
  if (art)
    return (
      <header className="flex flex-col gap-6 pt-6 pb-2 md:pt-10">
        <div className="flex flex-col gap-3">
          {breadcrumb}
          <Stage art={art} focus={artFocus} scrim="left" priority>
            {artAside && (
              <span aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
                {artAside}
              </span>
            )}
            <div className="relative flex flex-col gap-5 p-5 sm:flex-row sm:items-end sm:justify-between md:p-8">
              <div className="flex min-w-0 items-center gap-4 md:gap-5">
                {leading}
                <div className="flex min-w-0 flex-col gap-1">
                  {line && <p className="text-small font-medium text-white/75">{line}</p>}
                  <h1 className="text-title text-white text-balance md:text-page">{title}</h1>
                  {description && <div className="text-body text-white/80">{description}</div>}
                </div>
              </div>
              {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
            </div>
          </Stage>
        </div>
        {children}
      </header>
    )
  return (
    <header className="flex flex-col gap-6 pt-6 pb-2 md:pt-10">
      <div className="flex flex-col gap-3">
        {breadcrumb}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div className="flex min-w-0 items-center gap-4">
            {leading}
            <div className="flex min-w-0 flex-col gap-1">
              {line && <p className="text-small font-medium text-accent-text">{line}</p>}
              <h1 className="text-title text-ink text-balance md:text-page">{title}</h1>
              {description && <p className="text-body text-ink-2">{description}</p>}
            </div>
          </div>
          {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
        </div>
      </div>
      {children}
    </header>
  )
}

export type Crumb = { label: string; link?: LinkOptions }

export function Breadcrumbs({ items }: { items: Crumb[] }) {
  return (
    <nav aria-label="Brotkrumen">
      <ol className="flex flex-wrap items-center gap-1 text-small text-ink-3">
        {items.map((c, i) => {
          const last = i === items.length - 1
          return (
            <li key={i} className="flex items-center gap-1">
              {c.link && !last ? (
                <Link {...c.link} className="rounded-[4px] transition-colors hover:text-ink">
                  {c.label}
                </Link>
              ) : (
                <span aria-current={last ? 'page' : undefined} className={cx(last && 'text-ink-2')}>
                  {c.label}
                </span>
              )}
              {!last && <ChevronRight aria-hidden="true" className="size-3.5 text-ink-3/70" />}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}

type SectionProps = {
  heading: ReactNode
  description?: ReactNode
  /** One quiet action at the right of the heading ("Alle anzeigen", a ghost button). */
  action?: ReactNode
  /** h2 by default; sub-sections inside a section use h3. */
  level?: 2 | 3
  children: ReactNode
  className?: string
}

/** A titled block. Sections separate by 40 px of white space, never by boxes. */
export function Section({ heading, description, action, level = 2, children, className }: SectionProps) {
  const id = useId()
  const H = level === 2 ? 'h2' : 'h3'
  return (
    <section aria-labelledby={id} className={cx('not-first:mt-10', className)}>
      <div className="mb-4 flex items-end justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-0.5">
          <H id={id} className={cx('text-ink', level === 2 ? 'text-heading' : 'text-body font-semibold')}>
            {heading}
          </H>
          {description && <p className="text-small text-ink-2">{description}</p>}
        </div>
        {action && <div className="flex shrink-0 items-center gap-2">{action}</div>}
      </div>
      {children}
    </section>
  )
}
